import React from 'react';
import { Button, FormGroup, Input } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faSave } from '@fortawesome/free-solid-svg-icons';
import { util } from '../Util';
import { ApiRoutes } from '../ApiRoutes';
import { FormLabel, toasty } from '../common/forms/FormElements';
import _ from 'lodash-es';
import moment from 'moment';
import { MomentDateFormat } from '../MomentDateFormat';
import { LinearProgress } from '@mui/material';

export default class BrokerDateCapacityManager extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            editEnabled: false,
            updatedBrokerDateCapacity: {},
            anyChanges: false,
            brokerCapacityOverview: null,
            loading: false,
        };
    }

    componentDidMount() {
        this.populateState();
    }

    componentDidUpdate() {
        const { selectedBrokerDateCapacity } = this.props;
        const { brokerCapacityOverview } = this.state;

        if (
            selectedBrokerDateCapacity &&
            (!brokerCapacityOverview ||
                !moment(selectedBrokerDateCapacity.date).isSame(
                    brokerCapacityOverview.date,
                    'day'
                ))
        ) {
            this.populateState();
        }
    }

    populateState = async () => {
        const { loading } = this.state;

        const { selectedBrokerDateCapacity, companyId, brokerId } = this.props;

        if (loading || !selectedBrokerDateCapacity) {
            return;
        }

        this.setState({ loading: true });

        try {
            const brokerCapacityOverview = await util.fetch.js(
                ApiRoutes.brokerCapacityOverview.forDate(
                    companyId ?? brokerId,
                    moment(selectedBrokerDateCapacity.date).format(
                        MomentDateFormat.ApiUrlRequest
                    )
                )
            );

            this.setState({ brokerCapacityOverview });
        } finally {
            this.setState({ loading: false });
        }
    };

    onDateQuantityChange = (e) => {
        const { updatedBrokerDateCapacity } = this.state;

        updatedBrokerDateCapacity.maximumAssignmentCount = e.target.value;

        this.setState({ updatedBrokerDateCapacity, anyChanges: true });
    };

    onSaveDateCapacity = async () => {
        this.setState({ saving: true });

        const { updatedBrokerDateCapacity } = this.state;
        const { onDateEditFinished } = this.props;

        try {
            const brokerDateUpdateResponse = await util.fetch.andGetResponse(
                util.fetch.types.post,
                ApiRoutes.brokerDateCapacity.upsert(),
                updatedBrokerDateCapacity,
                'Failed to update date capacity',
                null,
                true
            );

            if (brokerDateUpdateResponse) {
                toasty.success('Date Capacity Updated');

                this.setState({
                    saving: false,
                    editEnabled: false,
                    anyChanges: false,
                });

                onDateEditFinished(Boolean(brokerDateUpdateResponse));
                return;
            }
        } catch (e) {
            console.error({ e });

            if (e?.message) {
                toasty.error('Invalid Capacity Configuration', e.message);
            }

            this.setState({ saving: false });
        }

        this.setState({ saving: false });
    };

    getInvalid = () => {
        const { updatedBrokerDateCapacity, brokerCapacityOverview } =
            this.state;

        return (
            !updatedBrokerDateCapacity ||
            !updatedBrokerDateCapacity.maximumAssignmentCount ||
            updatedBrokerDateCapacity.maximumAssignmentCount < 0 ||
            updatedBrokerDateCapacity.maximumAssignmentCount <
                brokerCapacityOverview.activeAssignmentCount
        );
    };

    toggleEditMode = () => {
        const { editEnabled } = this.state;
        const { onDateEditFinished } = this.props;

        if (!editEnabled) {
            const { selectedBrokerDateCapacity } = this.props;

            this.setState({
                updatedBrokerDateCapacity: _.cloneDeep(
                    selectedBrokerDateCapacity
                ),
                anyChanges: false,
            });
        } else {
            onDateEditFinished();
        }

        this.setState({ editEnabled: !editEnabled });
    };

    getLabelText = () => {
        const { selectedBrokerDateCapacity } = this.props;

        const { brokerCapacityOverview } = this.state;

        const displayDate = moment(selectedBrokerDateCapacity.date).format(
            MomentDateFormat.WeekDayNameAndDate
        );

        return brokerCapacityOverview ? (
            <>
                {displayDate} - currently,{' '}
                {brokerCapacityOverview.activeAssignmentCount} assignments
            </>
        ) : (
            <>{displayDate}</>
        );
    };

    render() {
        const { selectedBrokerDateCapacity } = this.props;

        if (!selectedBrokerDateCapacity) {
            return null;
        }

        const {
            editEnabled,
            updatedBrokerDateCapacity,
            saving,
            anyChanges,
            loading,
            brokerCapacityOverview,
        } = this.state;

        if (loading) {
            return <LinearProgress />;
        }

        const invalid = this.getInvalid();

        return (
            <div className="d-flex">
                <div className="w-50">
                    {!editEnabled && (
                        <FormGroup>
                            <FormLabel
                                text={this.getLabelText()}
                                htmlFor="maximumAssignmentCount"
                            />

                            <Input
                                type="number"
                                id="maximumAssignmentCount"
                                value={
                                    selectedBrokerDateCapacity.maximumAssignmentCount
                                }
                                disabled
                                readOnly
                            />
                        </FormGroup>
                    )}

                    {editEnabled && (
                        <FormGroup>
                            <FormLabel
                                text={this.getLabelText()}
                                htmlFor="maximumAssignmentCount"
                                required={true}
                            />

                            <Input
                                type="number"
                                min={
                                    brokerCapacityOverview.activeAssignmentCount
                                }
                                step={1}
                                id="maximumAssignmentCount"
                                onChange={this.onDateQuantityChange}
                                invalid={invalid}
                                value={
                                    updatedBrokerDateCapacity.maximumAssignmentCount
                                }
                            />
                        </FormGroup>
                    )}
                </div>

                <div className="align-content-center ml-auto">
                    <Button
                        type="button"
                        onClick={this.toggleEditMode}
                        color="warning"
                        outline={editEnabled}
                    >
                        <FontAwesomeIcon icon={faEdit} />
                        {editEnabled && <>Cancel Edit</>}
                        {!editEnabled && <>Edit</>}
                    </Button>

                    <Button
                        type="button"
                        onClick={this.onSaveDateCapacity}
                        color="info"
                        className="ml-5"
                        outline={!editEnabled}
                        disabled={!editEnabled || !anyChanges || saving}
                    >
                        <FontAwesomeIcon icon={faSave} pulse={saving} /> Save
                    </Button>
                </div>
            </div>
        );
    }
}
